/**
 * A span and what carries it (10 §2, mission 13). When every pier standing under a bridge's deck has been
 * destroyed, the deck comes down with them: the `bridge` prop is destroyed too - a `propDestroyed` like any
 * other, so the renderer takes the deck away and every system that asks "is it standing?" gets the answer.
 *
 * Which piers carry which bridge is read off the map, never listed by a mission: a `bridgePier` under the deck's
 * rectangle (the run turned by the bridge's yaw) carries it. A bridge with no pier of that kind under it - every
 * bridge outside mission 13 - can never come down.
 *
 * ROUND4 finishes it. The deck no longer merely vanishes: it leaves a broken span (`PropState.wreck`), whose
 * rubble blocks the lane, and `closedCrossings` (hazards/terrain.ts) shuts the ford under it for the garrison's
 * navigation as well as for the ranger. And whatever stood on the deck goes into the river with it - which is
 * the Silver Star deed of 10 §2: the convoy crosses on its clock, the piers go, and the vehicles go with them.
 */

import { type Ctx, isAlive } from '../ctx';
import { damageEnemy, destroyProp } from '../damage';
import { destroyOutright } from '../enemies/specials/kinds';
import { cos, sin } from '../math';
import type { Wreck } from '../statics';
import { VEHICLES } from '../tuning';
import type { MissionDef, PropDef, PropState } from '../types';

/** A pier's centre this far off the deck's centre line still stands under it: the deck's half-width and a little. */
const UNDER_DECK = 3.2;

interface Span {
  bridgeId: number;
  pierIds: number[];
}

/** Worked out once per mission: the definition never changes. */
const SPANS = new WeakMap<MissionDef, Span[]>();

/** Distance along the deck's run, and across it, in the deck's own frame. */
function deckFrame(bridge: PropDef, x: number, z: number): { along: number; across: number } {
  const dx = x - bridge.x;
  const dz = z - bridge.z;
  // A run lies along its local x; rotation about y carries that to (cos, -sin) in (x, z).
  return { along: dx * cos(bridge.yaw) - dz * sin(bridge.yaw), across: dx * sin(bridge.yaw) + dz * cos(bridge.yaw) };
}

function underDeck(bridge: PropDef, pier: PropDef): boolean {
  const { along, across } = deckFrame(bridge, pier.x, pier.z);
  return Math.abs(along) <= (bridge.length ?? 4) / 2 && Math.abs(across) <= UNDER_DECK;
}

/** Every bridge that stands on piers, with the piers that carry it. */
export function spansOf(def: MissionDef): Span[] {
  const known = SPANS.get(def);
  if (known) return known;
  const piers = def.props.filter((p) => p.kind === 'bridgePier');
  const spans: Span[] = [];
  for (const bridge of def.props) {
    if (bridge.kind !== 'bridge') continue;
    const pierIds = piers.filter((p) => underDeck(bridge, p)).map((p) => p.id);
    if (pierIds.length > 0) spans.push({ bridgeId: bridge.id, pierIds });
  }
  SPANS.set(def, spans);
  return spans;
}

/**
 * The rubble of every fallen span, for the movement resolver and the navigation grid: a low run of broken
 * deck across the lane that nobody - ranger or garrison - gets over. Colliders stay axis-aligned, so the
 * run is read off the deck's quarter turn.
 */
export function spanWrecks(def: MissionDef, props: readonly PropState[]): Wreck[] {
  const out: Wreck[] = [];
  for (const span of spansOf(def)) {
    const state = props.find((p) => p.id === span.bridgeId);
    if (state?.wreck !== 'brokenSpan') continue;
    const bridge = def.props.find((p) => p.id === span.bridgeId);
    if (!bridge) continue;
    const half = (bridge.length ?? 4) / 2;
    const alongZ = Math.abs(sin(bridge.yaw)) > 0.7;
    out.push({
      id: span.bridgeId,
      x: bridge.x,
      z: bridge.z,
      shape: 'box',
      hx: alongZ ? VEHICLES.spanWreckHalfWidth : half,
      hz: alongZ ? half : VEHICLES.spanWreckHalfWidth,
      h: VEHICLES.spanWreckHeight,
    });
  }
  return out;
}

/**
 * Whatever was on the deck when it let go goes into the river. Returns how many of them were vehicles -
 * counting one the same charges had just wrecked where it stood, because it went down with the span either way.
 */
function dropWhatIsOnIt(ctx: Ctx, bridge: PropDef): number {
  let vehicles = 0;
  for (const e of ctx.world.enemies) {
    const { along, across } = deckFrame(bridge, e.x, e.z);
    if (Math.abs(along) > (bridge.length ?? 4) / 2 || Math.abs(across) > VEHICLES.spanWreckHalfWidth) continue;
    if (e.vehicle !== undefined) vehicles++;
    if (!isAlive(e)) continue;
    // His charges brought it down: what it takes with it is his.
    if (e.vehicle !== undefined) destroyOutright(ctx, e, true);
    else damageEnemy(ctx, e, 99, true);
  }
  return vehicles;
}

/** Runs with the hazards, every ground tick: cheap - most maps have no span at all, the rest one. */
export function stepSpans(ctx: Ctx): void {
  const w = ctx.world;
  for (const span of spansOf(ctx.statics.def)) {
    const gone = (id: number): boolean => w.props.find((p) => p.id === id)?.destroyed === true;
    if (gone(span.bridgeId) || !span.pierIds.every(gone)) continue;
    const bridge = ctx.statics.def.props.find((p) => p.id === span.bridgeId);
    const rode = bridge ? dropWhatIsOnIt(ctx, bridge) : 0;
    destroyProp(ctx, span.bridgeId);
    const state = w.props.find((p) => p.id === span.bridgeId);
    // Not `removed`: a deck that falls leaves its broken run in the water for the renderer to draw.
    if (state) state.wreck = 'brokenSpan';
    if (rode > 0) w.objective.spanDeed = true;
  }
}
